import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AppStateService } from '../../../services/app-state.service';
import { ApiCmdService } from '../../../services/api-cmd.service';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatSidenavContent } from '@angular/material/sidenav';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { FiltersQuestionsComponent } from '../filters/filters-questions.component';
import { DataListQuestionsApiResponseDto, QueryListQuestionsApiRequestDto } from '@packages/api-cmd-type-definitions';
import { GetQuestionResponse } from '@packages/api-cmd-type-definitions/lib/questions/get-question.dto';
import { GetQuestionTextResponse } from '@packages/api-cmd-type-definitions/lib/questions/get-question-text.dto';
import { getAnswerTypeLabel } from '../../../constants/question.constants';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgIf } from '@angular/common';
import { HasRoleModule } from '../../../modules/has-role.module';
import { MatListItem } from '@angular/material/list';
import { MatSnackBar } from '@angular/material/snack-bar';
import { handlerSnackbarApiError } from '../../../utils/snackbar.handler';

@Component({
  selector: 'app-domande',
  standalone: true,
  imports: [
    MatSidenavContent,
    MatButton,
    RouterLink,
    MatTable,
    MatSort,
    MatHeaderCell,
    MatColumnDef,
    MatCell,
    MatHeaderRow,
    MatRow,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRowDef,
    MatRowDef,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    MatSortHeader,
    MatIconButton,
    MatIcon,
    MatProgressSpinner,
    NgIf,
    HasRoleModule,
    MatListItem,
  ],
  templateUrl: './list-questions.component.html',
  styleUrl: './list-questions.component.scss',
})
export class ListQuestionsComponent implements OnInit, OnDestroy {
  public router = inject(Router);
  public appStateService = inject(AppStateService);
  private apiCmdService = inject(ApiCmdService);
  displayedColumns: string[] = ['code', 'answerType', 'maxAnswers', 'questionTexts'];
  dataSource = new MatTableDataSource<any>([]);
  paginationSkip = 0;
  paginationLimit = 32;
  sortField = 'code';
  sortDirection: 'asc' | 'desc' = 'asc';
  public $legalEntityCodeSelected = toObservable(computed(() => this.appStateService.$legalEntityCodeSelected())).subscribe({
    next: value => {
      this.clearTable();
      this.loadData();
    },
    error: err => {
      console.error(err);
    },
  });
  private scrollSubscription: Subscription = new Subscription();
  private queryFilters: QueryListQuestionsApiRequestDto = {};
  protected readonly getAnswerTypeLabel = getAnswerTypeLabel;
  public isLoading = true;

  constructor(
    public dialog: MatDialog,
    private snackbar: MatSnackBar
  ) {}

  private setterIsLoading(value: boolean): () => void {
    return () => {
      this.isLoading = value;
    };
  }

  ngOnInit() {
    //this.loadData();
  }

  ngOnDestroy() {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
    this.resetSort();
    this.clearTable();
  }

  navigateToCreate() {
    this.router.navigate(['/domande/create']);
  }

  refreshList() {
    this.clearTable();
    this.loadData();
  }

  openFilterDialog(): void {
    const dialogRef = this.dialog.open(FiltersQuestionsComponent, { data: this.queryFilters });

    dialogRef.componentInstance.filtersApplied.subscribe((filters: any) => {
      this.applyFilters(filters);
      if (Object.keys(filters).length > 0) {
        this.clearTable();
        this.loadData();
      }
    });
  }

  clearTable() {
    this.paginationSkip = 0;
    this.dataSource.data = [];
  }
  resetSort() {
    this.sortField = 'code';
    this.sortDirection = 'asc';
  }

  applyFilters(filters: Record<string, any>): void {
    Object.keys(filters).forEach(attribute => {
      if (filters[attribute] === null || filters[attribute] === undefined || filters[attribute] === '') {
        // @ts-expect-error TS7053
        delete this.queryFilters[attribute];
      } else {
        // @ts-expect-error TS7053
        this.queryFilters[attribute] = filters[attribute];
      }
    });
  }

  loadData() {
    this.isLoading = true;
    this.apiCmdService
      .listQuestions({
        ...this.queryFilters,
        paginationSkip: this.paginationSkip,
        paginationLimit: this.paginationLimit,
        sortOrder: this.sortDirection,
        fieldOrder: this.sortField,
      })
      .subscribe({
        next: (response: DataListQuestionsApiResponseDto) => {
          this.dataSource.data = [
            ...this.dataSource.data,
            ...response.data.map((item: GetQuestionResponse): Omit<GetQuestionResponse, 'questionTexts'> & { questionTexts: string } => {
              const italianText = item.questionTexts.find((text: GetQuestionTextResponse) => text.languageCode === 'it') || item.questionTexts[0];
              if (italianText) {
                return { ...item, questionTexts: italianText.text };
              }
              return {
                ...item,
                questionTexts: '',
              };
            }),
          ];
          this.isLoading = false;
        },
        error: err => handlerSnackbarApiError(this.snackbar, err, [this.setterIsLoading(false)]),
      });
  }

  loadMoreData(event: any) {
    this.paginationSkip += this.paginationLimit;
    this.loadData();
  }

  sortData(sort: Sort) {
    this.sortField = sort.active;
    this.sortDirection = sort.direction || 'asc';
    this.clearTable();
    this.loadData();
  }

  onRowClick(event: MouseEvent, row: any) {
    if (window.getSelection()?.toString()) {
      return; // Do nothing if text is selected
    }

    this.router.navigate(['/domande/edit', row.id]);
  }

  onMouseUp(event: MouseEvent) {
    // This method is just to capture the mouseup event
  }
}
